import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, Sparkles, Check, FileText, Loader2 } from "lucide-react";

export default function UploadModal({ open, onClose, property, onDone }) {
  const [stage, setStage] = useState("pick"); // pick | extracting | confirm
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [fields, setFields] = useState({ cert_type: "", issue_date: "", expiry_date: "", engineer_name: "", served_date: "", rule_id: "" });
  const [rules, setRules] = useState([]);
  const inputRef = useRef();

  const reset = () => { setStage("pick"); setFile(null); setPreview(null); setPendingId(null); setRules([]); };
  const close = () => { reset(); onClose(); };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    if (f.type.startsWith("image/")) setPreview(URL.createObjectURL(f));
    else setPreview(null);
    setStage("extracting");
    const fd = new FormData();
    fd.append("property_id", property.id);
    fd.append("file", f);
    try {
      const { data } = await api.post("/certificates/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setPendingId(data.pending_id);
      setRules(data.rules || []);
      setFields({
        cert_type: ex.cert_type || "Other",
        issue_date: ex.issue_date || "",
        expiry_date: ex.expiry_date || "",
        engineer_name: ex.engineer_name || "",
        served_date: "",
        rule_id: data.suggested_rule_id || (data.rules?.[0]?.id ?? ""),
      });
      setStage("confirm");
      if (ex.error) toast.warning("Couldn't auto-read everything — please check the fields.");
      else toast.success("Details extracted — confirm below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setStage("pick");
    }
  };

  const confirm = async () => {
    if (!fields.rule_id) return toast.error("Select which requirement this covers");
    try {
      await api.post("/certificates/confirm", { property_id: property.id, pending_id: pendingId, ...fields, served_date: fields.served_date || null, issue_date: fields.issue_date || null, expiry_date: fields.expiry_date || null });
      toast.success("Certificate filed and compliance updated.");
      close();
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md bg-card" data-testid="upload-modal">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Sparkles size={18} className="text-emerald-brand" /> Smart certificate upload</DialogTitle></DialogHeader>

        {stage === "pick" && (
          <div>
            <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="file-input"
              onChange={(e) => handleFile(e.target.files[0])} />
            <button data-testid="upload-dropzone" onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-2xl p-10 flex flex-col items-center gap-3 hover:border-emerald-brand hover:bg-emerald-50/50 transition-colors">
              <UploadCloud size={40} className="text-emerald-brand" />
              <span className="font-semibold">Upload or photograph a certificate</span>
              <span className="text-sm text-muted-foreground">JPG, PNG or PDF. AI reads the dates for you.</span>
            </button>
            <p className="mt-3 text-xs text-muted-foreground text-center">Or forward it to your email-in address — it files itself.</p>
          </div>
        )}

        {stage === "extracting" && (
          <div className="py-12 flex flex-col items-center gap-4 text-center" data-testid="extracting-state">
            <div className="relative">
              <Loader2 size={44} className="animate-spin text-emerald-brand" />
            </div>
            <p className="font-semibold">Reading your certificate…</p>
            <p className="text-sm text-muted-foreground">Extracting type, dates and engineer with AI.</p>
          </div>
        )}

        {stage === "confirm" && (
          <div className="space-y-3" data-testid="confirm-state">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-secondary">
              {preview ? <img src={preview} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <FileText size={28} className="text-muted-foreground" />}
              <span className="text-sm truncate">{file?.name}</span>
            </div>
            <Field label="Certificate type"><input data-testid="field-cert-type" value={fields.cert_type} onChange={(e) => setFields({ ...fields, cert_type: e.target.value })} className="inp" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Issue date"><input data-testid="field-issue-date" type="date" value={fields.issue_date || ""} onChange={(e) => setFields({ ...fields, issue_date: e.target.value })} className="inp" /></Field>
              <Field label="Expiry date"><input data-testid="field-expiry-date" type="date" value={fields.expiry_date || ""} onChange={(e) => setFields({ ...fields, expiry_date: e.target.value })} className="inp" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Engineer / issuer"><input data-testid="field-engineer" value={fields.engineer_name || ""} onChange={(e) => setFields({ ...fields, engineer_name: e.target.value })} className="inp" /></Field>
              <Field label="Served to tenant on"><input data-testid="field-served-date" type="date" value={fields.served_date || ""} onChange={(e) => setFields({ ...fields, served_date: e.target.value })} className="inp" /></Field>
            </div>
            <Field label="Covers which requirement?">
              <select data-testid="field-rule" value={fields.rule_id} onChange={(e) => setFields({ ...fields, rule_id: e.target.value })} className="inp">
                {rules.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <button data-testid="confirm-cert" onClick={confirm} className="w-full min-h-[48px] rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold flex items-center justify-center gap-2 transition-colors">
              <Check size={18} /> Confirm & file
            </button>
          </div>
        )}
        <style>{`.inp{width:100%;min-height:44px;padding:0 .75rem;border-radius:.6rem;border:1px solid hsl(var(--input));background:hsl(var(--card))}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="text-xs font-medium text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>);
}
